import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { MatSort, MatPaginator } from '@angular/material';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { first } from 'rxjs/operators/first';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { combineLatest } from 'rxjs/observable/combineLatest';

import { format, isBefore, isDate } from 'date-fns';

import { DataTableSource } from '../../classes/DataTableSource/datatablesource';

import { ApplicationService, AdoptionRequestInfo, CollegeInfo } from '../../services/application/application.service';
import { AuthService } from '../../services/authentication/auth.service';
import { DialogService } from '../../services/dialogs/dialogs.service';
import { DownloadService, DownloadFormat, HttpMethod } from '../../services/download/download.service';
import { ErrorService } from '../../services/error/error.service';
import { SplashService } from '../../services/splash/splash.service';
import { UserInfo, UserService } from '../../services/user/user.service';

import { SchoolYearPipe } from '../../pipes/schoolyear.pipe';
import { TitleCasePipe } from '../../pipes/titlecase.pipe';
import { Tag, TagTypes } from '../ui/tags/tags.component';

@Component({
    selector: 'app-manage-adoptions',
    styleUrls: ['manageadoptions.component.css'],
    templateUrl: 'manageadoptions.component.html',
})
export class ManageAdoptionsComponent implements OnInit, OnDestroy {
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;

    private _subrelease: Subject<void> = new Subject<void>();

    AdoptionsData: DataTableSource<AdoptionRequestInfo>;
    Adoptions: AdoptionRequestInfo[];
    AllHeaders: string[] = ['RequestNumber', /*'Prefix',*/ 'Code', 'Name', 'Requestor', 'DateRequested', 'Status', 'Colleges', 'Actions'];
    AvailableColleges: CollegeInfo[];
    NoData: boolean;
    PartialViewReady: boolean;
    SmallDisplay: boolean;
    TagOptions: Tag[] | null;
    TagTypes = TagTypes;
    User: UserInfo;
    VisibleHeaders: string[];
    VisibleColumns: string[];

    constructor(
        private appService: ApplicationService,
        private authService: AuthService,
        private decimalPipe: DecimalPipe,
        private dialogService: DialogService,
        private downloadService: DownloadService,
        private errorService: ErrorService,
        private mediaChange: ObservableMedia,
        private schoolYearPipe: SchoolYearPipe,
        private splashService: SplashService,
        private titleCasePipe: TitleCasePipe,
        private userService: UserService
    ) {}

    ngOnInit() {
        const self = this;

        self.AdoptionsData = new DataTableSource<AdoptionRequestInfo>(
            [],
            self.paginator,
            self.sorter,
            self.SortAdoptionRequests.bind(self)
        );
        self.Adoptions = null;
        self.AvailableColleges = null;
        self.NoData = false;
        self.PartialViewReady = false;
        self.SmallDisplay = false;
        self.TagOptions = null;
        self.User = null;

        self.SmallDisplay = self.mediaChange.isActive('xs') || self.mediaChange.isActive('sm');

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subrelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
            });

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(user => user && !!user.AuthToken)
        ).subscribe(user => {
            self.User = user;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => {
                self.User = _user;
            });

            // Load static data for use in the compoment.
            const adoptionsObs = self.appService.GetAllAdoptionRequests(self.User.AuthToken, self.User.CurrentRole);
            const collegesObs = self.appService.GetAllColleges(self.User.AuthToken);

            combineLatest(adoptionsObs, collegesObs, function(adoptions, colleges) {
                return {
                    Adoptions: adoptions,
                    AvailableColleges: colleges,
                };
            })
                .pipe(takeUntil(self._subrelease))
                .subscribe(
                    packet => {
                        self.Adoptions = packet.Adoptions;
                        self.AvailableColleges = packet.AvailableColleges;
                        self.TagOptions = self.AvailableColleges.map((c: CollegeInfo) => new Tag(c.CollegeName, c.CollegeID, c.Active));

                        self.UpdateAdoptions(packet.Adoptions);

                        // Ready for component use.
                        self.PartialViewReady = true;
                    },
                    error => {
                        self.errorService.HandleServiceError(error);
                    }
                );
        });
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    // #region Approve Request Method

    ApproveRequest(crs: AdoptionRequestInfo) {
        const self = this;

        if (!crs) {
            return;
        }
        const colleges = (crs.Colleges || []).map(x => x.CollegeName).join(', ');

        // Get the user confirmation before proceeding.
        const message =
            `You are about to approve the following course adoption request:<br /><br />
        <strong>Course Code: </strong>${crs.Code}<br />
        <strong>Course Name/Title: </strong>${crs.Name}<br />
        <strong>Grad Requirement: </strong>${crs.GradRequirementName}<br />
        <strong>Credits: </strong>${self.decimalPipe.transform(crs.HSCredit, '1.1-1')}<br />
        <strong>Proposed Use: </strong>${crs.ProposedUseID === 0 ? 'Standard' : 'Other (' + crs.ProposedUseDescription + ')'}<br />
        <strong>School Year: </strong>${crs.SchoolYear}<br />
        <strong>Requestor: </strong>${self.titleCasePipe.transform(crs.RequestedByFirstName)} ${self.titleCasePipe.transform(
                crs.RequestedByLastName
            )}<br />
        <strong>School: </strong>${crs.SchoolName}<br />
        <strong>College(s): </strong>${colleges || ''}<br />
        <strong>Date Requested: </strong>${format(crs.DateRequested, 'M/D/YYYY h:mm A')}<br /><br />
        Once this course adoption request is approved, you will need to add the relevant college course prefix ` +
            `and code (ex: PHY 2049C) in the "Manage Courses" tool in order for DELTA to recognize the college ` +
            `course associated with this adoption request (${crs.Code}). Are you sure you wish to approve this course adoption request?`;

        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText: message,
                OKText: `Yes - Approve Adoption`,
                CancelText: 'No - CANCEL',
            })
            .subscribe(success => {
                if (success === true) {
                    // Approve adoption request
                    self.splashService
                        .DisplaySplash({ TitleText: 'Please Wait', MessageText: 'Approving Course Adoption Request...' })
                        .subscribe(() => {
                            self.appService
                                .ApproveAdoptionRequest(self.User.AuthToken, self.User.CurrentRole, crs.AdoptionRequestID)
                                .subscribe(
                                    reqs => {
                                        self.splashService.CloseSplash();

                                        // Snackbar success
                                        self.splashService.SnackbarSuccess('Course Adoption Request Approved Successfully.');

                                        // Update the list of requests
                                        self.UpdateAdoptions(reqs);
                                    },
                                    error => {
                                        self.errorService.HandleServiceError(error);
                                    }
                                );
                        });
                }
            });
    }

    // #endregion

    // #region Deny Request Method

    DenyRequest(crs: AdoptionRequestInfo) {
        const self = this;

        if (!crs) {
            return;
        }
        const colleges = (crs.Colleges || []).map(x => x.CollegeName).join(', ');

        // Get the user confirmation before proceeding.
        const message = `You are about to <strong>deny</strong> the following course adoption request:<br /><br />
        <strong>Course Code: </strong>${crs.Code}<br />
        <strong>Course Name/Title: </strong>${crs.Name}<br />
        <strong>Grad Requirement: </strong>${crs.GradRequirementName}<br />
        <strong>Credits: </strong>${self.decimalPipe.transform(crs.HSCredit, '1.1-1')}<br />
        <strong>Proposed Use: </strong>${crs.ProposedUseID === 0 ? 'Standard' : 'Other (' + crs.ProposedUseDescription + ')'}<br />
        <strong>School Year: </strong>${crs.SchoolYear}<br />
        <strong>Requestor: </strong>${self.titleCasePipe.transform(crs.RequestedByFirstName)} ${self.titleCasePipe.transform(
            crs.RequestedByLastName
        )}<br />
        <strong>School: </strong>${crs.SchoolName}<br />
        <strong>College(s): </strong>${colleges || ''}<br />
        <strong>Date Requested: </strong>${format(crs.DateRequested, 'M/D/YYYY h:mm A')}<br /><br />
        Are you sure you wish to deny this course adoption request?`;

        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText: message,
                OKText: `Yes - Deny Adoption`,
                CancelText: 'No - CANCEL',
            })
            .subscribe(success => {
                if (success === true) {
                    // Deny adoption request
                    self.splashService
                        .DisplaySplash({ TitleText: 'Please Wait', MessageText: 'Denying Course Adoption Request...' })
                        .subscribe(() => {
                            self.appService
                                .DenyAdoptionRequest(self.User.AuthToken, self.User.CurrentRole, crs.AdoptionRequestID)
                                .subscribe(
                                    reqs => {
                                        self.splashService.CloseSplash();

                                        // Snackbar success
                                        self.splashService.SnackbarSuccess('Course Adoption Request Denied Successfully.');

                                        // Update the list of requests
                                        self.UpdateAdoptions(reqs);
                                    },
                                    error => {
                                        self.errorService.HandleServiceError(error);
                                    }
                                );
                        });
                }
            });
    }

    // #endregion

    // #region Download Adoption Requests Spreadsheet Method

    DownloadAdoptionRequestsSpreadsheet() {
        const self = this;

        // Set splash
        self.splashService
            .DisplaySplash({ TitleText: 'Please wait', MessageText: 'Downloading Adoption Requests Spreadsheet...' })
            .pipe(takeUntil(self._subrelease))
            .subscribe(() => {
                self.downloadService
                    .RetrieveDownload(
                        HttpMethod.GET,
                        `Api/Services/Excel/GetCourseAdoptionRequestsSpreadsheet`,
                        {
                            AuthToken: self.User.AuthToken || '',
                            CurrentRole: self.User.CurrentRole || '',
                            Attachment: true,
                        },
                        DownloadFormat.Excel
                    )
                    .pipe(takeUntil(self._subrelease))
                    .subscribe(
                        success => {
                            // Drop the splash?
                            self.splashService.CloseSplash();
                        },
                        error => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
            });
    }

    // #endregion

    // #region Show Adoption Request Info Method

    ShowAdoptionRequestInfo(crs: AdoptionRequestInfo) {
        const self = this;
        const colleges = (crs.Colleges || []).map(x => x.CollegeName).join(', ');

        let msg = `<strong>Course Code: </strong>${crs.Code}<br />
        <strong>Course Name/Title: </strong>${crs.Name}<br />
        <strong>Grad Requirement: </strong>${crs.GradRequirementName}<br />
        <strong>Credits: </strong>${self.decimalPipe.transform(crs.HSCredit, '1.1-1')}<br />
        <strong>Proposed Use: </strong>${crs.ProposedUseID === 0 ? 'Standard' : 'Other (' + crs.ProposedUseDescription + ')'}<br />
        <strong>School Year: </strong>${self.schoolYearPipe.transform(crs.SchoolYear)}<br />
        <strong>Requestor: </strong>${self.titleCasePipe.transform(crs.RequestedByFirstName)} ${self.titleCasePipe.transform(
            crs.RequestedByLastName
        )}<br />
        <strong>School: </strong>${crs.SchoolName}<br />
        <strong>College(s): </strong>${colleges || ''}<br />
        <strong>Date Requested: </strong>${format(crs.DateRequested, 'M/D/YYYY h:mm A')}<br />
        <strong>Request Status: </strong>${crs.Approved == null ? 'Pending' : crs.Approved === true ? 'Approved' : 'Denied'}<br />
        `;

        if (crs.Approved != null) {
            msg += `<br /><strong>Reviewer: </strong>${self.titleCasePipe.transform(
                crs.ReviewedByFirstName
            )} ${self.titleCasePipe.transform(crs.ReviewedByLastName)}<br />
            <strong>Date Reviewed: </strong>${format(crs.DateReviewed, 'M/D/YYYY h:mm A')}`;
        }

        // Pop up a dialog.
        self.dialogService
            .OpenMessageBoxDialog({
                TitleText: 'Adoption Request Info',
                MessageText: msg,
                OKText: 'Done',
                height: '500px',
                width: '900px',
            })
            .subscribe();
    }

    // #endregion

    // #region Sort Adoption Requests Method

    SortAdoptionRequests(First: AdoptionRequestInfo, Second: AdoptionRequestInfo) {
        const self = this;

        let PropA: string | number | Date;
        let PropB: string | number | Date;

        switch (self.sorter.active) {
            case 'LastName':
                [PropA, PropB] = [First.RequestedByLastName, Second.RequestedByLastName];
                break;
            case 'FirstName':
                [PropA, PropB] = [First.RequestedByFirstName, Second.RequestedByFirstName];
                break;
            case 'Code':
                [PropA, PropB] = [First.Code, Second.Code];
                break;
            case 'DateRequested':
                [PropA, PropB] = [First.DateRequested, Second.DateRequested];
                break;
            case 'Status':
                [PropA, PropB] = [
                    First.Approved == null ? 'P' : First.Approved === true ? 'A' : 'D',
                    Second.Approved == null ? 'P' : Second.Approved === true ? 'A' : 'D',
                ];
                break;
        }

        if (isDate(PropA) || isDate(PropB)) {
            let baseSort = 0;

            if (isDate(PropA) && !isDate(PropB)) {
                baseSort = 1;
            } else if (isDate(PropB) && !isDate(PropA)) {
                baseSort = -1;
            } else if (isBefore(PropA, PropB)) {
                baseSort = -1;
            } else if (isBefore(PropB, PropA)) {
                baseSort = 1;
            }

            return baseSort * (self.sorter.direction === 'asc' ? 1 : -1);
        } else {
            const ValueA = PropA != null && PropA !== '' ? (isNaN(+PropA) ? PropA : +PropA) : PropA;
            const ValueB = PropB != null && PropB !== '' ? (isNaN(+PropB) ? PropB : +PropB) : PropB;

            return (ValueA < ValueB ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
        }
    }

    // #endregion

    // #region Update Adoptions Method

    UpdateAdoptions(requests: AdoptionRequestInfo[]) {
        const self = this;

        self.Adoptions = requests;

        if (!self.Adoptions || !self.Adoptions.length) {
            self.NoData = true;

            // Put in a fake record to trip the display message
            self.AdoptionsData.Update([{}]);
        } else {
            self.NoData = false;
            self.AdoptionsData.Update(self.Adoptions);
        }

        self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
        self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
    }

    // #endregion
}

// #region Custom Classes

// #endregion



// WEBPACK FOOTER //
// ./src/app/manageadoptions/manageadoptions.component.ts